import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Minus, Send } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../components/Input/Input";
import Notification from "../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./EnterpriseForm.module.scss";

const EnterpriseForm = () => {
  /* =========================
     STATE
  ========================= */
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [apis, setApis] = useState([{ apiMasterId: "", usage: "" }]);
  const [apiOptions, setApiOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  /* =========================
     NOTIFICATION
  ========================= */
  const showNotification = (msg, type = "success") => {
    setNotification(msg);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     FETCH DATA
  ========================= */
  const fetchApis = useCallback(async () => {
    try {
      const res = await apiGet("catalog", "/apimaster/categories-with-apis");
      const categories = res?.data || [];
      setApiOptions(categories);
    } catch {
      showNotification("Failed to load API list", "error");
    }
  }, []);

  const fetchuserdataApis = async () => {
    try {
      const response = await apiGet("user", "/profile");
      const userData = response?.data;
      if (userData?.companyName) {
        localStorage.setItem("companyName", userData.companyName);
      }
      setFullName(userData?.name || "");
      setMobile(userData?.mobile || "");
      setEmail(userData?.email || "");
    } catch (error) {
      console.log("Profile fetch error", error);
    }
  };

  useEffect(() => {
    fetchApis();
  }, [fetchApis]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const user = localStorage.getItem("user");

    if (token && user) {
      setIsLoggedIn(true);
      fetchuserdataApis();
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  /* =========================
     HELPERS
  ========================= */
  const addApiField = () => {
    setApis((prev) => [...prev, { apiMasterId: "", usage: "" }]);
  };

  const removeApiField = () => {
    setApis((prev) => prev.slice(0, -1));
  };

  const handleChange = (index, field, value) => {
    const updated = [...apis];
    updated[index][field] = value;
    setApis(updated);
    setErrors((prev) => ({ ...prev, [`${field}_${index}`]: "" }));
  };

  /* =========================
     VALIDATION
  ========================= */
  const validateForm = () => {
    const e = {};

    if (!fullName.trim()) e.fullName = "This field is required";

    if (!mobile?.trim()) {
      e.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(mobile)) {
      e.mobile = "Enter valid 10 digit mobile number";
    }

    if (!email?.trim()) {
      e.email = "Email Id is required";
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      e.email = "Enter valid email";
    }

    apis.forEach((api, i) => {
      if (!api.apiMasterId) e[`apiMasterId_${i}`] = "This field is required";
      if (!api.usage) e[`usage_${i}`] = "This field is required";
      if (api.usage && isNaN(api.usage))
        e[`usage_${i}`] = "Usage must be a number";
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* =========================
     SUBMIT
  ========================= */
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        fullName,
        mobile,
        email,
        apis: apis.map((api) => ({
          apiMasterId: api.apiMasterId,
          monthlyUsage: Number(api.usage),
        })),
      };

      await apiPost("catalog", "/custom/pricing/insert", payload);
      showNotification("Enterprise request submitted", "success");

      setTimeout(() => {
        navigate("/billing/pricing");
      }, 1000);
    } catch (error) {
      const msg = getErrorMessage(error, "Failed to submit request");
      showNotification(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     JSX
  ========================= */
  return (
    <div className={styles["admin-enter-form__page"]}>
      {notification && (
        <div className={styles["admin-enter-form__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER SECTION */}
      <div className={styles["admin-enter-form__header"]}>
        <Link
          to={isLoggedIn ? "/billing/pricing" : "/pricing"}
          className={styles["admin-enter-form__back-btn"]}
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </Link>
        <div className={styles["admin-enter-form__title-group"]}>
          <h2 className={styles["admin-enter-form__title"]}>
            Enterprise Plan Request
          </h2>
          <p className={styles["admin-enter-form__subtitle"]}>
            Tell us about your requirements and our team will prepare a custom
            quote.
          </p>
        </div>
      </div>

      {/* FORM CARD */}
      <div className={styles["admin-enter-form__card"]}>
        {/* PERSONAL DETAILS GRID */}
        <div className={styles["admin-enter-form__grid-3"]}>
          <Input
            label="Full Name"
            type="text"
            placeholder="Enter Full Name"
            required
            value={fullName}
            readOnly={isLoggedIn}
            onChange={(e) => {
              setFullName(e.target.value);
              setErrors((prev) => ({ ...prev, fullName: "" }));
            }}
            error={errors.fullName}
          />

          <Input
            label="Mobile"
            type="text"
            inputMode="numeric"
            placeholder="Enter Mobile"
            required
            readOnly={isLoggedIn}
            value={mobile}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                setMobile(value);
                setErrors((prev) => ({ ...prev, mobile: "" }));
              }
            }}
            error={errors.mobile}
          />

          <Input
            label="Email"
            type="email"
            placeholder="Enter Email"
            required
            readOnly={isLoggedIn}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: "" }));
            }}
            error={errors.email}
          />
        </div>

        {/* API CONFIGURATION LIST */}
        <div className={styles["admin-enter-form__api-list"]}>
          <h3 className={styles["admin-enter-form__section-title"]}>
            API Configuration
          </h3>

          {apis.map((item, index) => (
            <div className={styles["admin-enter-form__api-row"]} key={index}>
              {/* CUSTOM SELECT */}
              <div className={styles["admin-enter-form__input-group"]}>
                <label className={styles["admin-enter-form__label"]}>
                  API Name <span>*</span>
                </label>
                <select
                  className={`${styles["admin-enter-form__select"]} ${
                    errors[`apiMasterId_${index}`]
                      ? styles["admin-enter-form__select--error"]
                      : ""
                  }`}
                  value={item.apiMasterId}
                  onChange={(e) =>
                    handleChange(index, "apiMasterId", Number(e.target.value))
                  }
                >
                  <option value="">Select an API</option>
                  {apiOptions.map((category) => (
                    <optgroup
                      key={category.categoryName}
                      label={category.categoryName}
                    >
                      {category.apis.map((api) => (
                        <option key={api.id} value={api.id}>
                          {api.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {errors[`apiMasterId_${index}`] && (
                  <p className={styles["admin-enter-form__error-text"]}>
                    {errors[`apiMasterId_${index}`]}
                  </p>
                )}
              </div>

              {/* MONTHLY USAGE */}
              <Input
                label="Monthly Usage"
                type="text"
                placeholder="Estimated monthly hits"
                required
                value={item.usage}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    handleChange(index, "usage", value);
                  }
                }}
                error={errors[`usage_${index}`]}
              />

              {/* ACTIONS (Only show on last row) */}
              <div className={styles["admin-enter-form__api-actions"]}>
                {index === apis.length - 1 && (
                  <>
                    <button
                      className={`${styles["admin-enter-form__btn"]} ${styles["admin-enter-form__btn--secondary"]}`}
                      onClick={addApiField}
                      disabled={loading}
                      type="button"
                    >
                      <Plus size={16} strokeWidth={2.5} />
                      Add API
                    </button>

                    {apis.length > 1 && (
                      <button
                        className={`${styles["admin-enter-form__btn"]} ${styles["admin-enter-form__btn--danger"]}`}
                        onClick={removeApiField}
                        disabled={loading}
                        type="button"
                      >
                        <Minus size={16} strokeWidth={2.5} />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* SUBMIT */}
        <div className={styles["admin-enter-form__submit-row"]}>
          <button
            className={`${styles["admin-enter-form__btn"]} ${styles["admin-enter-form__btn--primary"]}`}
            onClick={handleSubmit}
            disabled={loading}
            style={{ padding: "0 32px" }}
          >
            <Send size={16} strokeWidth={2.5} />
            {loading ? "Submitting Request..." : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseForm;
